/**
 * ContentJobsTile — admin dashboard tile for the Content Engine job
 * queue (Wave 3). Shows real-time status counts, per-handler breakdown,
 * and a dead-letter inspector with one-click retry.
 *
 * Cost-safe: 1 GET to /admin/content-jobs/counts every 15s while the
 * tile is mounted, 1 GET to /admin/content-jobs?status=dead_letter
 * when the inspector dialog opens. No LLM calls.
 */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2,
    RefreshCw,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Activity,
    Inbox,
    Workflow,
    RotateCcw,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual mapping per status — `bg-bl-*` / `text-bl-*` tokens are all
// defined in index.css so we don't reintroduce the white-on-white bug
// class that v4.40.34-36 fixed.
const STATUS_META = {
    queued: {
        label: "Queued",
        icon: Clock,
        textCls: "text-bl-muted",
        bgCls: "bg-bl-bg",
    },
    running: {
        label: "Running",
        icon: Activity,
        textCls: "text-bl-teal",
        bgCls: "bg-bl-teal-tint",
    },
    succeeded: {
        label: "Succeeded",
        icon: CheckCircle2,
        textCls: "text-bl-green",
        bgCls: "bg-bl-green-soft",
    },
    failed: {
        label: "Failed (retrying)",
        icon: AlertTriangle,
        textCls: "text-bl-warn",
        bgCls: "bg-bl-amber-tint",
    },
    dead_letter: {
        label: "Dead-letter",
        icon: Inbox,
        textCls: "text-bl-rust",
        bgCls: "bg-bl-rust-tint",
    },
};

const STATUS_ORDER = ["running", "queued", "failed", "dead_letter", "succeeded"];


function StatusPill({ status, count }) {
    const meta = STATUS_META[status] || STATUS_META.queued;
    const Icon = meta.icon;
    return (
        <div
            className={`rounded-md px-3 py-2.5 ${meta.bgCls} flex flex-col gap-1`}
            data-testid={`content-jobs-pill-${status}`}
        >
            <div className="flex items-center gap-1.5">
                <Icon className={`h-3.5 w-3.5 ${meta.textCls}`} />
                <span className={`text-[11px] uppercase tracking-wide font-semibold ${meta.textCls}`}>
                    {meta.label}
                </span>
            </div>
            <span className="text-xl font-mono-bl text-bl">{count}</span>
        </div>
    );
}


function DeadLetterRow({ job, onRetry, retrying }) {
    // Truncate long error strings — the dialog has a fixed width and we
    // don't want a single stack trace pushing the retry button below the
    // fold. Full text is still available in the GET /content-jobs API.
    const error = job.last_error || "(no error message)";
    const truncatedError = error.length > 140 ? error.slice(0, 140) + "…" : error;
    return (
        <div
            className="border border-bl-line rounded-md p-3 space-y-2"
            data-testid={`dead-letter-row-${job.job_id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5 min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <code className="text-xs font-mono-bl text-bl-muted truncate">
                            {job.job_id}
                        </code>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-bl-cream text-bl-muted whitespace-nowrap">
                            {job.attempts}/{job.max_attempts} attempts
                        </span>
                    </div>
                    <div className="text-sm font-semibold text-bl">
                        {job.job_type}
                    </div>
                    {job.package_id && (
                        <div className="text-xs text-bl-muted font-mono-bl">
                            pkg: {job.package_id}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => onRetry(job.job_id)}
                    disabled={retrying}
                    className="bl-btn-ghost text-xs px-2.5 py-1.5 flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
                    data-testid={`dead-letter-retry-${job.job_id}`}
                >
                    {retrying ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <RotateCcw className="h-3.5 w-3.5" />
                    )}
                    Retry
                </button>
            </div>
            <div className="text-xs text-bl-rust leading-relaxed bg-bl-rust-tint rounded px-2 py-1.5 font-mono-bl">
                {truncatedError}
            </div>
        </div>
    );
}


export default function ContentJobsTile() {
    const [counts, setCounts] = useState(null);
    const [handlers, setHandlers] = useState([]);
    const [workerHealth, setWorkerHealth] = useState(null);
    const [workers, setWorkers] = useState([]);
    // Stuck-job detector — running jobs whose `started_at` is older
    // than the queue's STUCK_RUNNING_MINUTES threshold. Backend
    // surfaces these so we can offer a one-click "Recover" button
    // instead of waiting for the watchdog co-task's next sweep.
    const [stuckCount, setStuckCount] = useState(0);
    const [stuckThreshold, setStuckThreshold] = useState(30);
    const [recovering, setRecovering] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const [deadLetterJobs, setDeadLetterJobs] = useState([]);
    const [inspectorLoading, setInspectorLoading] = useState(false);
    const [retryingId, setRetryingId] = useState(null);

    async function loadCounts(silent = false) {
        if (!silent) setRefreshing(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/content-jobs/counts`,
                { withCredentials: true },
            );
            setCounts(data.counts || {});
            setHandlers(data.registered_handlers || []);
            setWorkerHealth(data.worker_health || null);
            setWorkers(data.workers || []);
            setStuckCount(Number(data.stuck_count) || 0);
            setStuckThreshold(Number(data.stuck_threshold_minutes) || 30);
        } catch (e) {
            if (!silent) {
                toast.error("Failed to load queue status");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }

    async function handleRecoverStuck() {
        setRecovering(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/content-jobs/recover-stuck`,
                {},
                {
                    withCredentials: true,
                    params: { older_than_minutes: stuckThreshold },
                },
            );
            const n = Number(data?.reset_count) || 0;
            if (n > 0) {
                toast.success(`Recovered ${n} stuck job${n === 1 ? "" : "s"} — re-queued for retry`);
            } else {
                toast.info("No stuck jobs to recover");
            }
            await loadCounts(true);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Failed to recover stuck jobs",
            );
        } finally {
            setRecovering(false);
        }
    }

    async function loadDeadLetter() {
        setInspectorLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/content-jobs?status=dead_letter&limit=50`,
                { withCredentials: true },
            );
            setDeadLetterJobs(data.items || []);
        } catch (e) {
            toast.error("Failed to load dead-letter queue");
            setDeadLetterJobs([]);
        } finally {
            setInspectorLoading(false);
        }
    }

    async function handleRetry(jobId) {
        setRetryingId(jobId);
        try {
            await axios.post(
                `${API}/admin/content-jobs/${jobId}/retry`,
                {},
                { withCredentials: true },
            );
            toast.success(`Re-queued ${jobId}`);
            // Optimistic update — remove from the dead-letter list in the
            // dialog and let the next counts poll repaint the tile.
            setDeadLetterJobs((prev) =>
                prev.filter((j) => j.job_id !== jobId),
            );
            await loadCounts(true);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Retry failed",
            );
        } finally {
            setRetryingId(null);
        }
    }

    useEffect(() => {
        loadCounts();
        // Refresh every 15s while the tile is mounted so admins see new
        // running/succeeded counts roll in as the worker processes jobs.
        const t = setInterval(() => loadCounts(true), 15000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (inspectorOpen) loadDeadLetter();
    }, [inspectorOpen]);

    if (loading) {
        return (
            <div
                className="bl-card p-6 flex items-center gap-3 text-bl-muted"
                data-testid="content-jobs-tile-loading"
            >
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading queue status…</span>
            </div>
        );
    }

    const deadLetterCount = (counts && counts.dead_letter) || 0;

    // Worker liveness badge — green if any worker beat within the cutoff,
    // red if all heartbeats are stale (or no worker has ever booted).
    // The badge also surfaces the freshest `last_seen` timestamp so an
    // admin can verify "yes the worker beat 4 seconds ago" without
    // opening a Mongo shell.
    const isHealthy = workerHealth && workerHealth.any_healthy;
    const healthLabel = workerHealth
        ? (isHealthy
            ? `${workerHealth.healthy_count} worker${workerHealth.healthy_count === 1 ? "" : "s"} live`
            : (workerHealth.total_count > 0 ? "worker stale" : "worker offline"))
        : "worker unknown";
    const lastSeen = workerHealth && workerHealth.last_seen
        ? new Date(workerHealth.last_seen)
        : null;
    const lastSeenRelative = lastSeen
        ? (() => {
            const sec = Math.max(0, Math.round((Date.now() - lastSeen.getTime()) / 1000));
            if (sec < 60) return `${sec}s ago`;
            if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
            return `${Math.round(sec / 3600)}h ago`;
        })()
        : "never";

    return (
        <div className="bl-card p-6 space-y-4" data-testid="content-jobs-tile">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                        <Workflow className="h-4 w-4 text-bl-teal" />
                        <h3 className="text-base font-semibold text-bl">
                            Content Engine Queue
                        </h3>
                        <span
                            className={`flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                                isHealthy
                                    ? "bg-bl-green-soft text-bl-green"
                                    : "bg-bl-rust-tint text-bl-rust"
                            }`}
                            data-testid="content-jobs-worker-badge"
                            title={`Last heartbeat: ${lastSeenRelative}`}
                        >
                            <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                    isHealthy ? "bg-bl-green animate-pulse" : "bg-bl-rust"
                                }`}
                            />
                            {healthLabel}
                        </span>
                    </div>
                    <p className="text-xs text-bl-muted">
                        {handlers.length} handler{handlers.length === 1 ? "" : "s"} ·
                        last beat {lastSeenRelative}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => loadCounts(false)}
                    disabled={refreshing}
                    className="bl-btn-ghost text-xs px-2 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="content-jobs-refresh-btn"
                    aria-label="Refresh queue status"
                >
                    <RefreshCw
                        className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
                    />
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {STATUS_ORDER.map((status) => (
                    <StatusPill
                        key={status}
                        status={status}
                        count={(counts && counts[status]) || 0}
                    />
                ))}
            </div>

            {stuckCount > 0 && (
                <button
                    type="button"
                    onClick={handleRecoverStuck}
                    disabled={recovering}
                    className="w-full bl-btn-secondary text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="content-jobs-recover-stuck-btn"
                    title={`${stuckCount} job${stuckCount === 1 ? "" : "s"} running >${stuckThreshold}m — likely SIGKILLed mid-task`}
                >
                    {recovering ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <RefreshCw className="h-4 w-4" />
                    )}
                    Recover {stuckCount} stuck job{stuckCount === 1 ? "" : "s"}
                    <span className="text-[10px] text-bl-muted">
                        (running &gt;{stuckThreshold}m)
                    </span>
                </button>
            )}

            {deadLetterCount > 0 && (
                <button
                    type="button"
                    onClick={() => setInspectorOpen(true)}
                    className="w-full bl-btn-secondary text-sm flex items-center justify-center gap-2"
                    data-testid="content-jobs-open-dead-letter-btn"
                >
                    <Inbox className="h-4 w-4" />
                    Inspect {deadLetterCount} dead-letter
                    {deadLetterCount === 1 ? " job" : " jobs"}
                </button>
            )}

            <details className="text-xs text-bl-muted" data-testid="content-jobs-handlers-details">
                <summary className="cursor-pointer hover:text-bl">
                    {handlers.length} registered handlers
                </summary>
                <ul className="mt-2 space-y-0.5 pl-2">
                    {handlers.map((h) => (
                        <li key={h} className="font-mono-bl">
                            • {h}
                        </li>
                    ))}
                </ul>
            </details>

            <Dialog open={inspectorOpen} onOpenChange={setInspectorOpen}>
                <DialogContent
                    className="max-w-2xl max-h-[80vh] overflow-y-auto"
                    data-testid="dead-letter-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Inbox className="h-5 w-5 text-bl-rust" />
                            Dead-letter inbox
                        </DialogTitle>
                        <DialogDescription>
                            Jobs that exhausted their retry budget. Click
                            Retry to send them back to the queue with a
                            fresh attempt counter.
                        </DialogDescription>
                    </DialogHeader>
                    {inspectorLoading ? (
                        <div className="flex items-center gap-2 py-8 text-bl-muted text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading dead-letter jobs…
                        </div>
                    ) : deadLetterJobs.length === 0 ? (
                        <div className="text-sm text-bl-green text-center py-8">
                            <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
                            Dead-letter queue is empty.
                        </div>
                    ) : (
                        <div className="space-y-2 pt-2">
                            {deadLetterJobs.map((job) => (
                                <DeadLetterRow
                                    key={job.job_id}
                                    job={job}
                                    onRetry={handleRetry}
                                    retrying={retryingId === job.job_id}
                                />
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
